import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useId, useState, type FormEvent, type RefObject } from "react";
import {
  ADVANCED_CHAT_MODELS,
  ADVANCED_MODELS,
  DEFAULT_CHAT_MODEL_LABEL,
  DEFAULT_MAP_MODEL_LABEL,
  DEFAULT_PARALLEL_MAPS,
  DEFAULT_SCREEN_MIN,
  MAX_PARALLEL_MAPS,
  type AdvancedSetup,
  type ScreenSetting,
} from "../datasetMode";

interface Draft {
  chatModel: string;
  model: string;
  maxConcurrent: string;
  screen: ScreenSetting;
  screenMin: string;
}

const SELECT_CLASS =
  "mt-1 flex h-9 w-full rounded-[3px] border border-zinc-200 bg-white px-2 text-base text-zinc-900";
const LABEL_CLASS = "text-sm font-medium text-zinc-900";
const HINT_CLASS = "mt-1 text-xs leading-5 text-zinc-500";

const toDraft = (value: AdvancedSetup): Draft => ({
  chatModel: value.chatModel ?? "",
  model: value.model ?? "",
  maxConcurrent: value.maxConcurrent === null ? "" : String(value.maxConcurrent),
  screen: value.screen,
  screenMin: value.screenMin === null ? "" : String(value.screenMin),
});

/** Return the setup, or the problem with the draft. */
const parseDraft = (draft: Draft): AdvancedSetup | string => {
  const parallel = draft.maxConcurrent.trim();
  const maxConcurrent = parallel === "" ? null : Number(parallel);
  if (
    maxConcurrent !== null &&
    (!Number.isInteger(maxConcurrent) || maxConcurrent < 1 || maxConcurrent > MAX_PARALLEL_MAPS)
  ) {
    return `Parallel papers must be a whole number from 1 to ${MAX_PARALLEL_MAPS}.`;
  }
  const passMark = draft.screenMin.trim();
  const screenMin = draft.screen !== "on" || passMark === "" ? null : Number(passMark);
  if (screenMin !== null && (!Number.isFinite(screenMin) || screenMin < 0 || screenMin > 1)) {
    return "The pass mark must be a number from 0 to 1.";
  }
  return {
    chatModel: draft.chatModel || null,
    model: draft.model || null,
    maxConcurrent,
    screen: draft.screen,
    screenMin,
  };
};

interface AdvancedSetupFormProps {
  value: AdvancedSetup;
  onCancel: () => void;
  onSave: (value: AdvancedSetup) => void;
}

const AdvancedSetupForm = ({ value, onCancel, onSave }: AdvancedSetupFormProps) => {
  const id = useId();
  const [draft, setDraft] = useState(() => toDraft(value));
  const [error, setError] = useState<string | null>(null);

  const update = (patch: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setError(null);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseDraft(draft);
    if (typeof parsed === "string") {
      setError(parsed);
      return;
    }
    onSave(parsed);
  };

  return (
    <form onSubmit={submit} noValidate>
      <DialogHeader>
        <DialogTitle>Advanced mode</DialogTitle>
        <DialogDescription>
          The agent model applies from your next message. The map settings
          apply to each new table map that the agent starts in your Datasets
          chats. A map keeps its settings when it resumes.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-5 space-y-4">
        <div>
          <label htmlFor={`${id}-chat-model`} className={LABEL_CLASS}>
            Main agent model
          </label>
          <select
            id={`${id}-chat-model`}
            className={SELECT_CLASS}
            value={draft.chatModel}
            onChange={(event) => update({ chatModel: event.target.value })}
            aria-describedby={`${id}-chat-model-hint`}
          >
            <option value="">Default ({DEFAULT_CHAT_MODEL_LABEL})</option>
            {ADVANCED_CHAT_MODELS.map((model) => (
              <option key={model.id} value={model.id}>
                {model.label}
              </option>
            ))}
          </select>
          <p id={`${id}-chat-model-hint`} className={HINT_CLASS}>
            The agent that you chat with. It plans the table and writes the
            rows, and its helpers use the same model.
          </p>
        </div>
        <div>
          <label htmlFor={`${id}-model`} className={LABEL_CLASS}>
            Map model
          </label>
          <select
            id={`${id}-model`}
            className={SELECT_CLASS}
            value={draft.model}
            onChange={(event) => update({ model: event.target.value })}
            aria-describedby={`${id}-model-hint`}
          >
            <option value="">Default ({DEFAULT_MAP_MODEL_LABEL})</option>
            {ADVANCED_MODELS.map((model) => (
              <option key={model.id} value={model.id}>
                {model.label}
              </option>
            ))}
          </select>
          <p id={`${id}-model-hint`} className={HINT_CLASS}>
            The model that reads each paper in a table map.
          </p>
        </div>
        <div>
          <label htmlFor={`${id}-parallel`} className={LABEL_CLASS}>
            Parallel papers
          </label>
          <Input
            id={`${id}-parallel`}
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_PARALLEL_MAPS}
            step={1}
            placeholder={`${DEFAULT_PARALLEL_MAPS} (default)`}
            value={draft.maxConcurrent}
            onChange={(event) => update({ maxConcurrent: event.target.value })}
            aria-describedby={`${id}-parallel-hint`}
            className="mt-1"
          />
          <p id={`${id}-parallel-hint`} className={HINT_CLASS}>
            The number of papers that a map reads at the same time, from 1 to{" "}
            {MAX_PARALLEL_MAPS}.
          </p>
        </div>
        <div>
          <label htmlFor={`${id}-screen`} className={LABEL_CLASS}>
            Jev screen
          </label>
          <select
            id={`${id}-screen`}
            className={SELECT_CLASS}
            value={draft.screen}
            onChange={(event) => update({ screen: event.target.value as ScreenSetting })}
            aria-describedby={`${id}-screen-hint`}
          >
            <option value="server">Server setting</option>
            <option value="on">On</option>
            <option value="off">Off: the map reads every paper</option>
          </select>
          <p id={`${id}-screen-hint`} className={HINT_CLASS}>
            Jev checks each paper first, and the map reads only the papers
            that pass. The server setting can differ between staging and
            production.
          </p>
        </div>
        {draft.screen === "on" && (
          <div>
            <label htmlFor={`${id}-pass`} className={LABEL_CLASS}>
              Pass mark
            </label>
            <Input
              id={`${id}-pass`}
              type="number"
              inputMode="decimal"
              min={0}
              max={1}
              step={0.05}
              placeholder={`${DEFAULT_SCREEN_MIN} (default)`}
              value={draft.screenMin}
              onChange={(event) => update({ screenMin: event.target.value })}
              aria-describedby={`${id}-pass-hint`}
              className="mt-1"
            />
            <p id={`${id}-pass-hint`} className={HINT_CLASS}>
              A paper passes when its Jev score is at or above this mark, from
              0 to 1. A lower mark lets more papers through.
            </p>
          </div>
        )}
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
      </div>
      <DialogFooter className="mt-6">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Save</Button>
      </DialogFooter>
    </form>
  );
};

interface AdvancedSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: AdvancedSetup;
  onSave: (value: AdvancedSetup) => void;
  /** Where focus goes when the dialog closes. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export const AdvancedSetupDialog = ({
  open,
  onOpenChange,
  value,
  onSave,
  returnFocusRef,
}: AdvancedSetupDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="max-w-md"
      onCloseAutoFocus={(event) => {
        if (!returnFocusRef?.current) return;
        event.preventDefault();
        returnFocusRef.current.focus();
      }}
    >
      <AdvancedSetupForm
        value={value}
        onCancel={() => onOpenChange(false)}
        onSave={(next) => {
          onSave(next);
          onOpenChange(false);
        }}
      />
    </DialogContent>
  </Dialog>
);
